import { isTelcoUser } from 'src/utils/auth-utils/checkTelcoUser';
import { UserTypes } from 'src/utils/constants/stringConstants';
import { getGuestUserToken } from 'src/utils/UtilityMethods';

import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '../../browserStorageHandler';
import { DynamicObject } from '../../commonTypes';
import {
  GUEST_TOKEN,
  MIXPANEL_SILENT_LOGIN,
  USER_ID,
  USER_MAIL,
  USER_MOB
} from '../../constants/localStorageConstants';
// eslint-disable-next-line
import {
  clevertapUserProfilePush,
  createCleverTapUserData
} from '../clevertap/clevertapHelper';
import { gtm } from '../gtm';
import {
  MIXPANEL_AGE,
  MIXPANEL_CURRENT_SUBSCRIPTION,
  MIXPANEL_DEKEY,
  MIXPANEL_EVENTS_CTAS,
  MIXPANEL_EVENTS_SILENT_LOGIN,
  MIXPANEL_EVENTS_SSO_Failure,
  MIXPANEL_EVENTS_SSO_SUCCESS,
  MIXPANEL_FREE_TRIAL_EXPIRY_DATE,
  MIXPANEL_FREE_TRIAL_PACKAGE,
  MIXPANEL_GENDER,
  MIXPANEL_HAS_EDUAURAA,
  MIXPANEL_HAS_RENTAL,
  MIXPANEL_IS_EDUAURAA,
  MIXPANEL_IS_EMAIL_PRESENT,
  MIXPANEL_IS_MOBILE_PRESENT,
  MIXPANEL_IS_PWA,
  MIXPANEL_IS_RENTAL,
  MIXPANEL_IS_TALAMOOS,
  MIXPANEL_IS_UPGRADE,
  MIXPANEL_ISB2B,
  MIXPANEL_LATEST_SUBSCRIPTION_PACK,
  MIXPANEL_LATEST_SUBSCRIPTION_PACK_EXPIRY,
  MIXPANEL_NEW_APP_LANGUAGE,
  MIXPANEL_NEW_AUTOPLAY_SETTING,
  MIXPANEL_NEW_CONTENT_LANGUAGE,
  MIXPANEL_NEXT_EXPIRING_PACK,
  MIXPANEL_NEXT_PACK_EXPIRY_DATE,
  MIXPANEL_NOT_AVAILABLE,
  MIXPANEL_PACK_DURATION,
  MIXPANEL_PARENT_CONTROL_SETTING,
  MIXPANEL_PARTNER_NAME,
  MIXPANEL_PLATFORM_NAME,
  MIXPANEL_PWA_APP_VERSION,
  MIXPANEL_REGISTRING_COUNTRY,
  MIXPANEL_SEARCH_SUCCESS,
  MIXPANEL_SESSION_ID,
  MIXPANEL_SUBTITLES,
  MIXPANEL_SUCCESS,
  MIXPANEL_UNIQUE_ID,
  MIXPANEL_USER_TYPE,
  MIXPANEL_VIDEO_AUTOPLAYED
} from './mixpanelConstants';

type DefferedEvents = { [key: string]: any };
class Mixpanel {
  constructor() {
    this.setProperty(MIXPANEL_PWA_APP_VERSION, process.env.APP_VERSION);
    this.setProperty(MIXPANEL_PLATFORM_NAME, 'Web');
  }

  private static instance: Mixpanel;

  properties = new Map<string, unknown>();

  isOptimoveRegistered = false;
  defferedEvents: DefferedEvents = [];
  isPeoplePropertySet = false;
  isSuperPropertyRegistered = false;

  defaultFalseProperties = [
    MIXPANEL_CURRENT_SUBSCRIPTION,
    MIXPANEL_HAS_RENTAL,
    MIXPANEL_IS_PWA,
    MIXPANEL_IS_RENTAL,
    MIXPANEL_IS_TALAMOOS,
    MIXPANEL_SEARCH_SUCCESS,
    MIXPANEL_SUBTITLES,
    MIXPANEL_SUCCESS,
    MIXPANEL_VIDEO_AUTOPLAYED,
    MIXPANEL_IS_UPGRADE,
    MIXPANEL_IS_EDUAURAA,
    MIXPANEL_HAS_EDUAURAA,
    MIXPANEL_IS_EMAIL_PRESENT,
    MIXPANEL_IS_MOBILE_PRESENT,
    MIXPANEL_ISB2B
  ];

  /**
   * The static method that controls the access to the singleton instance.
   *
   * This implementation let you subclass the Mixpanel class while keeping
   * just one instance of each subclass around.
   */
  public static getInstance(): Mixpanel {
    if (!Mixpanel.instance) {
      Mixpanel.instance = new Mixpanel();
    }
    return Mixpanel.instance;
  }

  getProperty(key: string) {
    const mixpanelClonedObject = Object.assign(this);
    const value = this.properties.get(key) || mixpanelClonedObject[key];
    if (value) return value;
    if (this.defaultFalseProperties.includes(key)) return false;
    return MIXPANEL_NOT_AVAILABLE;
  }

  setProperty(key: string, value: unknown) {
    return this.properties.set(key, value);
  }

  setMultipleProperties(propertiesObj: Map<string, unknown>) {
    this.properties = {
      ...this.properties,
      ...propertiesObj
    };
  }

  async getUserToken() {
    return getLocalStorage(USER_ID) || (await getGuestUserToken()) || '';
  }

  isMobilePresentForLoggedIn = () => {
    try {
      const mobile = getLocalStorage(USER_MOB) ? getLocalStorage(USER_MOB) : '';
      return mobile.trim().length !== 0;
    } catch (error) {
      console.error('Failure to get user details from local storage');
    }
    return false;
  };

  isEmailPresentForLoggedIn = () => {
    try {
      const email = getLocalStorage(USER_MAIL)
        ? getLocalStorage(USER_MAIL)
        : '';
      return email.trim().length !== 0;
    } catch (error) {
      console.error('Failure to get user details from local storage');
    }
    return false;
  };

  trackEvent(eventName: string, trackObject: DynamicObject) {
    if (window && window.navigator && window.navigator.onLine) {
      window?.mixpanel?.track(eventName, trackObject);
      this.optimoveTracker(eventName, trackObject);
    } else {
      const trackOfflineAnalytics =
        getLocalStorage('trackOfflineAnalytics') &&
        JSON.parse(getLocalStorage('trackOfflineAnalytics'));
      if (trackOfflineAnalytics && Array.isArray(trackOfflineAnalytics)) {
        const offlineTrackObject = { ...trackObject, 'Offline Tracking': true };
        trackOfflineAnalytics.push({ eventName, offlineTrackObject });
        setLocalStorage(
          'trackOfflineAnalytics',
          JSON.stringify(trackOfflineAnalytics)
        );
      }
    }
  }
  optimoveTracker(
    eventName: string,
    trackObject: { [index: string]: unknown }
  ) {
    if (typeof window !== 'undefined' && window.isOptimoveInitialised) {
      if (!this.isOptimoveRegistered) {
        if (getLocalStorage(USER_ID)) {
          window.optimoveSDK.API.setUserId(
            getLocalStorage(USER_ID).toUpperCase()
          );
          this.isOptimoveRegistered = true;
        } else if (getLocalStorage(GUEST_TOKEN)) {
          window.optimoveSDK.API.setUserId(
            getLocalStorage(GUEST_TOKEN).toUpperCase()
          );
          this.isOptimoveRegistered = true;
        }
      }
      this.optimoveRegisteredCheck(trackObject, eventName);
    }
  }

  optimoveRegisteredCheck(
    trackObject: { [index: string]: unknown },
    eventName: string
  ) {
    if (this.isOptimoveRegistered) {
      [
        MIXPANEL_UNIQUE_ID,
        MIXPANEL_SESSION_ID,
        MIXPANEL_USER_TYPE,
        MIXPANEL_NEXT_EXPIRING_PACK,
        MIXPANEL_NEXT_PACK_EXPIRY_DATE,
        MIXPANEL_FREE_TRIAL_EXPIRY_DATE,
        MIXPANEL_PACK_DURATION,
        MIXPANEL_FREE_TRIAL_PACKAGE,
        MIXPANEL_REGISTRING_COUNTRY,
        MIXPANEL_GENDER,
        MIXPANEL_LATEST_SUBSCRIPTION_PACK,
        MIXPANEL_LATEST_SUBSCRIPTION_PACK_EXPIRY,
        MIXPANEL_NEW_AUTOPLAY_SETTING,
        MIXPANEL_NEW_APP_LANGUAGE,
        MIXPANEL_NEW_CONTENT_LANGUAGE,
        MIXPANEL_PARENT_CONTROL_SETTING,
        MIXPANEL_AGE,
        MIXPANEL_PLATFORM_NAME,
        MIXPANEL_IS_PWA,
        MIXPANEL_DEKEY,
        MIXPANEL_ISB2B
      ].forEach((key) => {
        trackObject[key] = this.getProperty(key);
      });
      const newEventName = eventName.replace(/ /g, '_').toLowerCase();
      const newTrackObject: DynamicObject = {};
      Object.keys(trackObject).forEach((key) => {
        newTrackObject[
          key.replace(/ /g, '_').toLowerCase()
        ] = `${trackObject[key]}`;
      });
      // Remove series data for CTAs event.
      if (eventName === MIXPANEL_EVENTS_CTAS && newTrackObject.series) {
        delete newTrackObject.series;
      }
      window.optimoveSDK.API.reportEvent(newEventName, newTrackObject);
    }
  }
  queueMixpanelEvents(eventName: string, trackObject: any, count = 0) {
    if (this.isMixpanelRegisterationPending()) {
      if (count < 3) {
        setTimeout(
          () => this.queueMixpanelEvents(eventName, trackObject, count++),
          3000
        );
      } else {
        this.defferedEvents.push({ eventName, trackObject });
      }
    } else if (typeof window !== 'undefined' && window.mixpanel) {
      this.trackEvent(eventName, trackObject);
    }
  }

  isMixpanelRegisterationPending() {
    return (
      !this.isPeoplePropertySet ||
      !this.isSuperPropertyRegistered ||
      this.getProperty(MIXPANEL_USER_TYPE) === undefined ||
      this.notAvailableCheck() ||
      (this.getProperty(MIXPANEL_USER_TYPE) === 'premium' &&
        !isTelcoUser() &&
        (this.getProperty(MIXPANEL_PACK_DURATION) === MIXPANEL_NOT_AVAILABLE ||
          this.getProperty(MIXPANEL_REGISTRING_COUNTRY) ===
            MIXPANEL_NOT_AVAILABLE)) ||
      (this.getProperty(MIXPANEL_USER_TYPE) === 'premium' &&
        isTelcoUser() &&
        this.getProperty(MIXPANEL_REGISTRING_COUNTRY) ===
          MIXPANEL_NOT_AVAILABLE) ||
      (this.getProperty(MIXPANEL_USER_TYPE) === 'register' &&
        this.getProperty(MIXPANEL_REGISTRING_COUNTRY) ===
          MIXPANEL_NOT_AVAILABLE) ||
      this.getProperty(MIXPANEL_PARTNER_NAME) === ''
    );
  }
  notAvailableCheck() {
    // AGE and Gender field check removed as not required in the guest flow
    return [
      MIXPANEL_USER_TYPE,
      MIXPANEL_UNIQUE_ID,
      MIXPANEL_NEW_AUTOPLAY_SETTING,
      MIXPANEL_NEW_APP_LANGUAGE,
      MIXPANEL_NEW_CONTENT_LANGUAGE
    ].some((item) => this.getProperty(item) === MIXPANEL_NOT_AVAILABLE);
  }

  // method logs the deffered events to the mixpanel /> used via window
  triggerDefferedEvents() {
    if (typeof window !== 'undefined' && window.mixpanel) {
      if (
        this.defferedEvents.length &&
        this.isPeoplePropertySet &&
        this.isSuperPropertyRegistered &&
        !this.notAvailableCheck()
      ) {
        if (
          this.getProperty(MIXPANEL_USER_TYPE) === UserTypes.GUEST ||
          (this.getProperty(MIXPANEL_USER_TYPE) === UserTypes.REGISTER &&
            this.getProperty(MIXPANEL_REGISTRING_COUNTRY) !==
              MIXPANEL_NOT_AVAILABLE) ||
          (isTelcoUser() &&
            this.getProperty(MIXPANEL_REGISTRING_COUNTRY) !==
              MIXPANEL_NOT_AVAILABLE) ||
          (this.getProperty(MIXPANEL_USER_TYPE) === UserTypes.PREMIUM &&
            this.getProperty(MIXPANEL_PACK_DURATION) !==
              MIXPANEL_NOT_AVAILABLE &&
            this.getProperty(MIXPANEL_REGISTRING_COUNTRY) !==
              MIXPANEL_NOT_AVAILABLE) ||
          this.getProperty(MIXPANEL_PARTNER_NAME) !== '' ||
          this.getProperty(MIXPANEL_DEKEY) !== ''
        ) {
          this.defferedEvents.forEach((item: any) => {
            gtm.triggerDefferedEvent(
              this.getProperty(MIXPANEL_UNIQUE_ID),
              this.getProperty(MIXPANEL_USER_TYPE),
              this.getProperty(MIXPANEL_NEW_APP_LANGUAGE),
              this.getProperty(MIXPANEL_NEW_CONTENT_LANGUAGE),
              this.getProperty(MIXPANEL_PARTNER_NAME),
              this.getProperty(MIXPANEL_DEKEY)
            );
            this.trackEvent(item.eventName, item.trackObject);
            // const clevertapEventObj = clevertapFilterMethod(item.trackObject);
            // clevertapEventPush(item.eventName, clevertapEventObj);
            if (getLocalStorage(MIXPANEL_SILENT_LOGIN)) {
              const eventTrackObject = JSON.parse(
                getLocalStorage(MIXPANEL_SILENT_LOGIN)
              );
              deleteLocalStorage(MIXPANEL_SILENT_LOGIN);
              this.trackEvent(MIXPANEL_EVENTS_SILENT_LOGIN, eventTrackObject);
              const eventName = eventTrackObject.Success
                ? MIXPANEL_EVENTS_SSO_SUCCESS
                : MIXPANEL_EVENTS_SSO_Failure;
              this.trackEvent(eventName, eventTrackObject);
            }
          });
          /* Clevertap script for profile push without identity */
          const userDataObj = createCleverTapUserData(undefined, true);
          clevertapUserProfilePush(userDataObj);
          this.defferedEvents = [];
        }
      }
    }
  }
}

export const mixpanelObject: DynamicObject = Mixpanel.getInstance();
